import { useEffect, useMemo, useState } from "react";
import {
  AdvancedMarker,
  Map as GoogleMap,
  useMap,
} from "@vis.gl/react-google-maps";
import {
  Clock,
  ClipboardCheck,
  ListOrdered,
  Loader2,
  Route as RouteIcon,
  Settings,
  Truck,
  UserPlus,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { Task, Trip, Waypoint } from "@/api.types.v2";
import { useComputeRoute, useTripWaypoints } from "@/api/trips";
import { RouteSettingsDialog } from "@/features/trips/route-settings-dialog";
import { RouteStartTimeDialog } from "@/features/trips/route-start-time-dialog";
import { PickupChecklistDialog } from "@/features/trips/pickup-checklist-dialog";
import { ReadinessChip } from "@/features/dispatch/readiness-chip";
import { getTaskCareSignals } from "@/features/dispatch/selectors";
import { getOperatorDisplayName, getOperatorInitials } from "@/lib/safe-access";
import { StatusPill } from "./atoms";
import { ModalStopList } from "./modal-stops";
import { buildStops, type DerivedStop } from "./trip-stops";
import { emptyLaneCounts, type LaneCounts, taskLane } from "./task-type-meta";
import { WaypointMarker } from "./markers/marker-visuals";

interface TripFocusModalProps {
  trip: Trip | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function parseLatLng(wp: Waypoint): { lat: number; lng: number } | null {
  if (wp.location) return wp.location;
  const [lat, lng] = (wp.locationKey ?? "").split(",").map(Number);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
}

function laneCountsOf(tasks: Task[]): LaneCounts {
  const c = emptyLaneCounts();
  for (const t of tasks) c[taskLane(t.type)] += 1;
  return c;
}

function markerStatus(stop: DerivedStop): "pending" | "active" | "done" {
  return stop.status;
}

/** Focused map: this trip's stops (redesigned markers) + planned route, fit. */
function FocusTripMap({
  trip,
  waypoints,
  stops,
}: {
  trip: Trip;
  waypoints: Waypoint[];
  stops: DerivedStop[];
}) {
  const map = useMap();
  const color = trip.color ?? "#6366f1";
  const stopById = useMemo(() => new Map(stops.map((s) => [s.id, s])), [stops]);

  useEffect(() => {
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    let any = false;
    waypoints.forEach((wp) => {
      const p = parseLatLng(wp);
      if (p) {
        bounds.extend(p);
        any = true;
      }
    });

    let polyline: google.maps.Polyline | null = null;
    const encoded = trip.plannedRoute?.routes?.[0]?.polyline?.encodedPolyline;
    if (encoded) {
      try {
        const path = google.maps.geometry.encoding.decodePath(encoded);
        polyline = new google.maps.Polyline({
          path,
          strokeColor: color,
          strokeOpacity: 0.9,
          strokeWeight: 4,
        });
        polyline.setMap(map);
        path.forEach((pt) => bounds.extend(pt));
        any = true;
      } catch {
        /* malformed polyline — skip */
      }
    }
    if (any) map.fitBounds(bounds, 56);
    return () => polyline?.setMap(null);
  }, [map, waypoints, trip.plannedRoute, color]);

  return (
    <>
      {waypoints.map((wp) => {
        const p = parseLatLng(wp);
        const stop = stopById.get(wp.id);
        if (!p || !stop) return null;
        const tasks = wp.tasks ?? [];
        return (
          <AdvancedMarker key={wp.id} position={p}>
            <WaypointMarker
              seq={wp.sequence}
              color={color}
              status={markerStatus(stop)}
              counts={laneCountsOf(tasks)}
              note={tasks.some((t) => getTaskCareSignals(t).note)}
              fragile={tasks.some((t) => getTaskCareSignals(t).fragile)}
              tier="near"
            />
          </AdvancedMarker>
        );
      })}
    </>
  );
}

function ToolButton({
  icon,
  label,
  onClick,
  primary,
  busy,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  primary?: boolean;
  busy?: boolean;
}) {
  return (
    <Button
      onClick={onClick}
      disabled={busy}
      variant={primary ? "default" : "outline"}
      className={
        "h-8 gap-1.5 rounded-lg px-2.5 text-[12px] font-semibold " +
        (primary
          ? "bg-indigo-600 text-white hover:bg-indigo-700"
          : "border-zinc-200 text-zinc-700 hover:bg-zinc-50")
      }
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
      {label}
    </Button>
  );
}

/**
 * Single-trip focus modal (dispatch redesign §6). Large overlay: focused map
 * (this trip's redesigned markers + route) beside the redesigned stop list,
 * with a toolbar whose Optimize runs the real route computation.
 */
export function TripFocusModal({
  trip,
  open,
  onOpenChange,
}: TripFocusModalProps) {
  const waypointQueries = useTripWaypoints(trip ? [trip] : []);
  const waypoints = useMemo(
    () => waypointQueries[0]?.data ?? [],
    [waypointQueries]
  );
  const stops = useMemo(() => buildStops(waypoints), [waypoints]);
  const driver = trip?.operator;
  const color = trip?.color ?? "#6366f1";

  const [reorderMode, setReorderMode] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [startTimeOpen, setStartTimeOpen] = useState(false);

  const compute = useComputeRoute({
    onSuccess: () =>
      toast.success("Route computed", { description: "Stops re-sequenced." }),
    onError: () =>
      toast.error("Couldn't compute route", {
        description: "Try again in a moment.",
      }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex flex-col gap-0 overflow-hidden p-0"
        style={{
          width: "min(1120px, 96vw)",
          maxWidth: "96vw",
          height: "min(720px, 92vh)",
        }}
      >
        <DialogTitle className="sr-only">
          Trip #{trip?.tripNumber} details
        </DialogTitle>
        <DialogDescription className="sr-only">
          Review this trip's route and stops.
        </DialogDescription>
        {trip && (
          <div className="flex min-h-0 flex-1 flex-col">
            {/* header */}
            <div className="flex items-center gap-3 border-b border-zinc-200 px-4 py-3">
              <span
                className="rounded"
                style={{ width: 5, height: 30, background: color }}
              />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[17px] font-bold text-zinc-900">
                    #{trip.tripNumber}
                  </span>
                  <StatusPill status={trip.status} />
                </div>
                <div className="text-[11px] text-zinc-500">Focus trip</div>
              </div>
              <div className="mx-1 h-9 w-px bg-zinc-200" />
              {driver ? (
                <div className="flex items-center gap-2">
                  <div
                    className="grid h-[30px] w-[30px] place-items-center rounded-full text-[11px] font-bold text-white"
                    style={{ background: color }}
                  >
                    {getOperatorInitials(driver)}
                  </div>
                  <div>
                    <div className="text-[12.5px] font-bold text-zinc-900">
                      {getOperatorDisplayName(driver)}
                    </div>
                    {trip.vehicle?.code && (
                      <div className="flex items-center gap-1 text-[10.5px] text-zinc-500">
                        <Truck className="h-3 w-3" />
                        {trip.vehicle.code}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-[12px] font-semibold text-amber-700">
                  <UserPlus className="h-3.5 w-3.5" /> No driver assigned
                </span>
              )}
              <div className="flex-1" />
              <ReadinessChip trip={trip} waypointCount={waypoints.length} />
              <div className="w-6" />
            </div>

            {/* toolbar */}
            <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 bg-zinc-50/50 px-4 py-2.5">
              <ToolButton
                icon={<Zap className="h-3.5 w-3.5" />}
                label="Optimize route"
                primary
                busy={compute.isPending}
                onClick={() => compute.mutate({ tripId: trip.id })}
              />
              <ToolButton
                icon={<ListOrdered className="h-3.5 w-3.5" />}
                label="Reorder"
                primary={reorderMode}
                onClick={() => setReorderMode((v) => !v)}
              />
              <ToolButton
                icon={<ClipboardCheck className="h-3.5 w-3.5" />}
                label="Pickup checklist"
                onClick={() => setChecklistOpen(true)}
              />
              <ToolButton
                icon={<Settings className="h-3.5 w-3.5" />}
                label="Route settings"
                onClick={() => setSettingsOpen(true)}
              />
              <ToolButton
                icon={<Clock className="h-3.5 w-3.5" />}
                label="Start time"
                onClick={() => setStartTimeOpen(true)}
              />
              <div className="flex-1" />
              <div className="flex items-center gap-3 text-[11.5px] text-zinc-600">
                {trip.distance != null && (
                  <span className="flex items-center gap-1">
                    <RouteIcon className="h-3.5 w-3.5 text-zinc-400" />
                    <b className="tabular-nums">{trip.distance.toFixed(1)}</b>{" "}
                    km
                  </span>
                )}
                {trip.estimatedDuration != null && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-zinc-400" />
                    <b className="tabular-nums">
                      {Math.floor(trip.estimatedDuration / 60)}h{" "}
                      {trip.estimatedDuration % 60}m
                    </b>
                  </span>
                )}
              </div>
            </div>

            {/* body: focused map + redesigned stop list */}
            <div className="grid min-h-0 flex-1 grid-cols-[1.15fr_1fr]">
              <div className="relative border-r border-zinc-200">
                <GoogleMap
                  defaultCenter={{ lat: 43.6532, lng: -79.3832 }}
                  defaultZoom={12}
                  gestureHandling="greedy"
                  disableDefaultUI
                  mapId="3667d2e0a1e93d2b"
                  style={{ width: "100%", height: "100%" }}
                >
                  <FocusTripMap
                    trip={trip}
                    waypoints={waypoints}
                    stops={stops}
                  />
                </GoogleMap>
                <div
                  className="absolute left-3 top-3 flex items-center gap-1.5 rounded-lg bg-white/95 px-2.5 py-1.5 text-[11px] font-semibold text-zinc-700"
                  style={{ boxShadow: "0 1px 3px rgba(15,15,15,.12)" }}
                >
                  <span
                    className="rounded-[3px]"
                    style={{ width: 10, height: 10, background: color }}
                  />
                  #{trip.tripNumber} · {waypoints.length} stops
                </div>
              </div>
              <div className="flex min-h-0 flex-col bg-white">
                <div className="flex items-center gap-2 border-b border-zinc-100 px-3.5 py-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                    {stops.length} stops ·{" "}
                    {stops.reduce((n, s) => n + s.total, 0)} tasks
                  </span>
                  {reorderMode && (
                    <span className="ml-auto text-[11px] font-semibold text-indigo-600">
                      Drag to reorder
                    </span>
                  )}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-2 py-1.5">
                  <ModalStopList
                    stops={stops}
                    color={color}
                    tripId={trip.id}
                    reorder={reorderMode}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {trip && (
        <>
          <PickupChecklistDialog
            tripId={trip.id}
            isOpen={checklistOpen}
            onOpenChange={setChecklistOpen}
          />
          <RouteSettingsDialog
            tripId={trip.id}
            isOpen={settingsOpen}
            onOpenChange={setSettingsOpen}
          />
          <RouteStartTimeDialog
            isOpen={startTimeOpen}
            onOpenChange={setStartTimeOpen}
            onSubmit={(startTime) => {
              compute.mutate({
                tripId: trip.id,
                customStartTime: startTime.toISOString(),
              });
              setStartTimeOpen(false);
            }}
          />
        </>
      )}
    </Dialog>
  );
}
